import React from "react";
import {
  BLEED, C, FONT, DataCenter, Draw, Worker, clamp, lerp, seg, smooth, ease, mixColor, rng,
} from "../kit";

// Act V · Main Street (the bridge).
//
//   p 0 – 0.3    Built up. Empty lots at dusk. Brick courses stack up row by row
//                while masons work the scaffolds; each storefront lights its
//                sign as it tops out, night falls, and SITE 7 draws itself in
//                behind the rooftops in one cold stroke.
//   p 0.3 – 0.6  Torn down. A shop at a time, the light stutters out, its warmth
//                runs down the wire into SITE 7, and its bricks fall out of the
//                facade course by course into a heap of rubble.
//   p 0.6 – 1    The screens. Cold screens pop up in the gaps and the rubble and
//                multiply, every one selling the same thing, until they all say
//                MORE.
//
// Beats are keyed to p so the act stretches with the timeline; flicker, falling
// bricks, tickers and pulses run on seconds so nothing ever freezes.

const GROUND = 660; // building line
const CURB = 692;
const HORIZON = 612; // far ground, seen between buildings
const CH = 9; // a course of brick, mortar included
const BL = 22; // a brick, head joint included
const GRAV = 1500; // falling bricks, units/s²

// ---------------------------------------------------------------------------
// Little helpers
// ---------------------------------------------------------------------------

// Deterministic 0..1 noise for flicker.
const hash = (n) => {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

// A light that holds steady until `at` (seconds), stutters, then dies.
function failing(local, at, seed, len = 1.1) {
  const d = local - at;
  if (d <= 0) return 1;
  if (d >= len) return 0;
  const k = d / len;
  return hash(Math.floor(d * 15) + seed * 13) > 0.15 + 0.75 * k ? 1 - 0.45 * k : 0.04;
}

// A light that stutters on at `at` (seconds) and then holds.
function kindle(local, at, seed, len = 0.75) {
  const d = local - at;
  if (d <= 0) return 0;
  if (d >= len) return 1;
  const k = d / len;
  return hash(Math.floor(d * 16) + seed * 29) < 0.25 + 0.75 * k ? 0.55 + 0.45 * k : 0.05;
}

// Overshoots a touch and settles: screens popping up.
const backOut = (k) => 1 + 2.70158 * Math.pow(k - 1, 3) + 1.70158 * Math.pow(k - 1, 2);

const R = (x, y, w, h) => `M ${x} ${y} h ${w} v ${h} h ${-w} Z`;
// A circle as path data, so many dots can share one element.
const dot = (x, y, r) => `M ${(x - r).toFixed(1)} ${y.toFixed(1)} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 `;
const poly = (pts) => pts.map(([x, y], i) => `${i ? "L" : "M"} ${x} ${y}`).join(" ");
const f1 = (v) => Math.round(v * 10) / 10;
// A box with half-extents hw x hh centred on (x, y), turned by a radians.
function box(x, y, hw, hh, a) {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const pt = (u, v) => `${f1(x + u * c - v * s)} ${f1(y + u * s + v * c)}`;
  return `M ${pt(-hw, -hh)} L ${pt(hw, -hh)} L ${pt(hw, hh)} L ${pt(-hw, hh)} Z `;
}
const inPoly = (pts, x, y) => {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
};
const inBox = ([bx, by, bw, bh], x, y) => x > bx && x < bx + bw && y > by && y < by + bh;
const boxPts = (x, y, w, h) => [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
const hole = (r, pad = 1.5) => [r.x - pad, r.y - pad, r.w + 2 * pad, r.h + 2 * pad];

const INK = {
  facadeDusk: "#2e1d15",
  facade: "#141010",
  facadeCold: "#0b1018",
  mortarDusk: "#4c3425",
  mortar: "#221b18",
  mortarCold: "#18222d",
  fresh: "#b06d3c",
  glass: "#0b0c0e",
  glassCold: "#0d151e",
  rim: "#6a4628",
  rimCold: "#6f90ad",
  signOff: "#25282c",
  sil: "#120d0a",
  rubble: "#0d0b0a",
  ink: "#050505", // scaffolds and masons
};

// ---------------------------------------------------------------------------
// The street. build is [first course, topping out] and tear is [lights out,
// how much of the wall is left standing], both as fractions of the act.
// ---------------------------------------------------------------------------

const SHOPS = [
  { id: "diner", x: 140, w: 185, h: 246, cap: "cornice", sign: "DINER", size: 22, seed: 3, ups: [1, 0, 1], build: [0.07, 0.18], tear: [0.37, 0.38] },
  { id: "hardware", x: 325, w: 180, h: 292, cap: "stepped", sign: "HARDWARE", size: 16, seed: 1, ups: [0, 1, 0], build: [0, 0.19], tear: [0.3, 0] },
  { id: "barber", x: 505, w: 135, h: 238, cap: "pediment", sign: "BARBER", size: 15, seed: 4, ups: [1, 0], build: [0.1, 0.195], tear: [0.405, 0] },
  { id: "feed", x: 640, w: 185, h: 272, cap: "gable", sign: "FEED & SEED", size: 15, seed: 2, ups: [0, 1, 0], build: [0.03, 0.185], tear: [0.335, 0.12] },
];
const THEATER = { id: "theater", x: 825, w: 205, h: 318, seed: 5, build: [0.08, 0.225], tear: [0.44, 0.3] };
const BAR = { id: "bar", x: 1030, w: 135, h: 250, seed: 7, ups: [1, 1], build: [0.12, 0.215], tear: [0.48, 0.5] };
const CHURCH = { id: "church", x: 1185, w: 130, seed: 6, build: [0.05, 0.235], out: 0.545 };
const HOUSE = { id: "house", x: 1345, w: 160, wall: 548, peak: 476, seed: 8, build: [0.14, 0.23], tear: [0.515, 0], bl: 56 };
const FILLER = { id: "filler", x: -260, w: 400, h: 232, seed: 9, build: [0.09, 0.17], out: 0.29 };

function outline({ x, w, h, cap }) {
  const top = GROUND - h;
  switch (cap) {
    case "stepped":
      return [[x, top + 26], [x + w * 0.16, top + 26], [x + w * 0.16, top + 14], [x + w * 0.32, top + 14],
        [x + w * 0.32, top], [x + w * 0.68, top], [x + w * 0.68, top + 14], [x + w * 0.84, top + 14],
        [x + w * 0.84, top + 26], [x + w, top + 26]];
    case "pediment":
      return [[x, top + 20], [x + w * 0.24, top + 20], [x + w * 0.5, top], [x + w * 0.76, top + 20], [x + w, top + 20]];
    case "gable":
      return [[x, top + 34], [x + w * 0.2, top + 34], [x + w * 0.2, top + 22], [x + w * 0.36, top + 22],
        [x + w * 0.5, top], [x + w * 0.64, top + 22], [x + w * 0.8, top + 22], [x + w * 0.8, top + 34], [x + w, top + 34]];
    case "raised":
      return [[x, top + 16], [x + w * 0.3, top + 16], [x + w * 0.3, top], [x + w * 0.7, top], [x + w * 0.7, top + 16], [x + w, top + 16]];
    default:
      return [[x, top], [x + w, top]];
  }
}

const facadePts = (b) => [[b.x, GROUND], ...outline(b), [b.x + b.w, GROUND]];
const facadeD = (b) => poly(facadePts(b)) + " Z";

const GEO = Object.fromEntries(SHOPS.map((s) => {
  const door = s.id === "barber" ? 24 : 30;
  const pole = s.id === "barber" ? 16 : 0;
  const win = { x: s.x + 12, y: GROUND - 112, w: s.w - 24 - door - 8 - pole, h: 90 };
  const n = s.ups.length;
  return [s.id, {
    top: GROUND - s.h,
    win,
    door: { x: s.x + s.w - 12 - door, y: GROUND - 118, w: door, h: 118 },
    sign: { x: s.x + 10, y: GROUND - 154, w: s.w - 20, h: 30 },
    ups: s.ups.map((_, i) => ({ x: s.x + (s.w * (i + 1)) / (n + 1) - 12, y: GROUND - 228, w: 24, h: 46 })),
    face: facadeD(s),
    rim: poly(outline(s)),
  }];
}));

const HW_ITEMS = (() => {
  const r = rng(77);
  const out = [];
  [0.3, 0.6].forEach((sy) => {
    let u = 0.04;
    while (u < 0.88) {
      const w = 0.05 + r() * 0.09;
      const h = 0.07 + r() * 0.12;
      out.push([u, sy - h, w, h]);
      u += w + 0.015 + r() * 0.05;
    }
  });
  return out;
})();

// A filled feed sack lying flat: a pillow shape.
const sack = (x, y, w, h) =>
  `M ${x} ${y + 2} Q ${x + w / 2} ${y - 2} ${x + w} ${y + 2} Q ${x + w + 2} ${y + h / 2} ${x + w} ${y + h - 1} ` +
  `Q ${x + w / 2} ${y + h + 1} ${x} ${y + h - 1} Q ${x - 2} ${y + h / 2} ${x} ${y + 2} Z `;

// What you see through each lit shop window, as dark shapes.
const INTERIOR = {
  diner: ({ x, y, w, h }) => {
    let d = R(x, y + h * 0.5, w, h * 0.12);
    for (let i = 0; i < 5; i++) {
      const sx = x + w * (0.12 + i * 0.19);
      d += R(sx - 6, y + h * 0.72, 12, 3) + R(sx - 1, y + h * 0.72 + 3, 2, h * 0.28 - 3);
    }
    for (let i = 0; i < 3; i++) {
      const lx = x + w * (0.2 + i * 0.3);
      d += R(lx - 0.5, y, 1, h * 0.13) + `M ${lx - 6} ${y + h * 0.2} L ${lx - 2.5} ${y + h * 0.13} L ${lx + 2.5} ${y + h * 0.13} L ${lx + 6} ${y + h * 0.2} Z `;
    }
    return d;
  },
  hardware: ({ x, y, w, h }) => {
    let d = R(x, y + h * 0.3, w, 2.5) + R(x, y + h * 0.6, w, 2.5) + R(x, y + h * 0.84, w, h * 0.16);
    HW_ITEMS.forEach(([u, v, iw, ih]) => { d += R(x + u * w, y + v * h, iw * w, ih * h); });
    return d;
  },
  barber: ({ x, y, w, h }) => {
    const cx = x + w * 0.5;
    // the chair in profile, facing left, under a hanging lamp
    return R(cx - 11, y + h - 3, 22, 3) + R(cx - 3, y + h * 0.72, 6, h * 0.28)
      + R(cx - 13, y + h * 0.6, 24, h * 0.11)
      + poly([[cx + 5, y + h * 0.64], [cx + 12, y + h * 0.3], [cx + 18, y + h * 0.31], [cx + 11, y + h * 0.66]]) + " Z "
      + R(cx + 11, y + h * 0.22, 9, 5) + R(cx - 15, y + h * 0.52, 15, 3) + R(cx - 14, y + h * 0.52, 3, h * 0.09)
      + R(cx - 14, y + h * 0.7, 2, h * 0.12) + R(cx - 24, y + h * 0.81, 11, 2.5)
      + R(cx - 2.5, y, 1, h * 0.1) + `M ${cx - 9} ${y + h * 0.17} L ${cx - 5} ${y + h * 0.1} L ${cx} ${y + h * 0.1} L ${cx + 4} ${y + h * 0.17} Z `;
  },
  feed: ({ x, y, w, h }) => {
    // sacks stacked on a pallet, and a platform scale
    const bw = 24;
    let d = R(x + 4, y + h - 5, bw * 3 + 8, 5);
    for (let row = 0; row < 4; row++) {
      for (let i = 0; i < 3 - (row > 1 ? 1 : 0); i++) {
        d += sack(x + 6 + i * (bw + 1.5) + (row % 2) * (bw / 2) * (row > 1 ? 1 : 0.4), y + h - 18 - row * 13, bw, 12);
      }
    }
    const sx = x + w - 16;
    d += R(sx - 9, y + h - 5, 20, 5) + R(sx - 1, y + h * 0.42, 3, h * 0.55) + `M ${sx - 6} ${y + h * 0.42} a 7 7 0 1 1 14 0 a 7 7 0 1 1 -14 0 Z `;
    return d;
  },
};
SHOPS.forEach((s) => { GEO[s.id].interior = INTERIOR[s.id](GEO[s.id].win); });
const BARBER_POLE = { x: GEO.barber.win.x + GEO.barber.win.w + 5, y: GROUND - 104 };

// The other fronts on the street, built once.
const TH_CAP = { ...THEATER, cap: "raised" };
const TH_FACE = facadeD(TH_CAP);
const TH_RIM = poly(outline(TH_CAP));
const TH_BX = THEATER.x + THEATER.w / 2;
const MQ = { x: THEATER.x + 17, y: 528, w: THEATER.w - 34, h: 42 };
// Marquee bulbs in three chase groups, one path each.
const BULBS = [0, 1, 2].map((c) => {
  let d = "";
  for (let i = 0; i < 19; i++) {
    const bx = MQ.x + 5 + (i * (MQ.w - 10)) / 18;
    if (i % 3 === c) d += dot(bx - MQ.x, 4.5, 1.9);
    if ((i + 1) % 3 === c) d += dot(bx - MQ.x, MQ.h - 4.5, 1.9);
  }
  return d;
});
const BAR_FACE = facadeD(BAR);
const BAR_WIN = { x: BAR.x + 14, y: 556, w: 68, h: 76 };
const BAR_DOOR = { x: BAR.x + BAR.w - 44, y: 548, w: 30, h: 112 };
const BAR_UPS = BAR.ups.map((_, i) => ({ x: BAR.x + (BAR.w * (i + 1)) / 3 - 12, y: GROUND - 228, w: 24, h: 46 }));
const FILLER_FACE = facadeD({ ...FILLER, cap: "flat" });
const FILLER_HOLES = [[-200, 438, 24, 46], [-120, 438, 24, 46], [-40, 438, 24, 46], [40, 438, 24, 46], [20, 548, 96, 90]];
const FILLER_GLASS = FILLER_HOLES.map(([x, y, w, h]) => R(x, y, w, h)).join(" ");

const CLAPBOARD = Array.from({ length: 13 }, (_, i) => `M ${HOUSE.x} ${HOUSE.wall + 8 + i * 8} h ${HOUSE.w}`).join(" ");
const ROOF_PTS = [[HOUSE.x - 12, HOUSE.wall + 5], [HOUSE.x + HOUSE.w / 2, HOUSE.peak], [HOUSE.x + HOUSE.w + 12, HOUSE.wall + 5]];
const ROOF = poly(ROOF_PTS);
const HWIN = { x: 1372, y: 566, w: 90, h: 80 };
const HDOOR = { x: 1474, y: 588, w: 22, h: 72 };

const arch = (x, y, w, h) => `M ${x} ${y + h} L ${x} ${y + w / 2} A ${w / 2} ${w / 2} 0 0 1 ${x + w} ${y + w / 2} L ${x + w} ${y + h} Z`;
const CHG = (() => {
  const { x, w } = CHURCH;
  const cx = x + w / 2;
  return {
    cx,
    bodyPts: [[x, GROUND], [x, 470], [cx, 412], [x + w, 470], [x + w, GROUND]],
    body: poly([[x, GROUND], [x, 470], [cx, 412], [x + w, 470], [x + w, GROUND]]) + " Z",
    tower: R(cx - 15, 352, 30, 120) + R(cx - 19, 318, 38, 36) + `M ${cx - 20} 320 L ${cx} 238 L ${cx + 20} 320 Z M ${cx - 1} 238 v -12 h 2 v 12 Z`,
    rim: poly([[x, 470], [cx, 412], [x + w, 470]]) + ` M ${cx - 20} 320 L ${cx} 238 L ${cx + 20} 320`,
    belfry: arch(cx - 8, 326, 16, 22),
    louvre: `M ${cx - 8} 337 h 16 M ${cx - 8} 342 h 16 M ${cx - 8} 347 h 16`,
    main: arch(cx - 13, 492, 26, 86),
    side: arch(x + 16, 508, 14, 58) + " " + arch(x + w - 30, 508, 14, 58),
    lead: `M ${cx} 492 V 578 M ${cx - 13} 520 H ${cx + 13} M ${cx - 13} 550 H ${cx + 13}`,
    door: arch(cx - 11, 614, 22, 46),
  };
})();

// ---------------------------------------------------------------------------
// Bricks. Every front is a stack of courses. Each course knows which bricks
// are wall (not glass), the order they let go in the teardown and how they
// fall (about half tip out toward the street; the rest drop inside). A wall
// torn down to a stub keeps a broken stair of bricks on top, each one resting
// on another, and leaves a heap in front of it.
// ---------------------------------------------------------------------------

function lot(o) {
  const n = Math.ceil((GROUND - o.top) / CH);
  const remRow = o.tear ? Math.round(o.tear[1] * n) : n;
  const bl = o.bl || BL;
  const r = rng(o.seed * 97 + 13);
  const rows = [];
  for (let i = 0; i < n; i++) {
    const yb = GROUND - i * CH;
    const ym = yb - CH / 2;
    const spans = [];
    let a = null;
    for (let x = Math.floor(o.x0); x <= o.x1; x++) {
      const solid = x < o.x1 && o.polys.some((pp) => inPoly(pp, x + 0.5, ym)) && !o.holes.some((h) => inBox(h, x + 0.5, ym));
      if (solid && a === null) a = x;
      else if (!solid && a !== null) {
        spans.push([a, x]);
        a = null;
      }
    }
    const off = i % 2 === 0 ? bl / 2 : 0;
    const bricks = [];
    spans.forEach(([sa, sb]) => {
      for (let j = Math.floor((sa - off) / bl) * bl + off; j < sb; j += bl) {
        const b0 = Math.max(sa, j + 1);
        const b1 = Math.min(sb, j + bl);
        if (b1 - b0 >= 4) {
          bricks.push({ x0: b0, x1: b1, ord: r(), vx: (r() - 0.5) * 34, vy: (r() - 0.35) * 24, spin: (r() - 0.5) * 7, out: r() < 0.5, stay: false });
        }
      }
    });
    rows.push({ y0: yb - CH + 1, y1: yb, bricks });
  }
  if (o.tear) {
    const odds = [0.8, 0.55, 0.38, 0.18];
    for (let i = remRow; i < Math.min(n, remRow + odds.length); i++) {
      rows[i].bricks.forEach((b) => {
        const held = i === remRow || rows[i - 1].bricks.some((c) => c.stay && c.x0 < b.x1 - 6 && c.x1 > b.x0 + 6);
        b.stay = held && r() < odds[i - remRow];
      });
    }
  }
  const out = { ...o, n, remRow, rows, faceD: o.polys.map((pp) => poly(pp) + " Z").join(" ") };
  if (o.tear) {
    const mr = rng(o.seed * 53 + 5);
    const maxH = 12 + 0.17 * (n - remRow) * CH;
    const xa = o.x0 - 14;
    const xb = o.x1 + 14;
    const hAt = (u) => maxH * Math.pow(Math.max(0, Math.sin(Math.PI * u)), 0.65);
    out.mound = Array.from({ length: 23 }, (_, i) => {
      const u = i / 22;
      const jag = i > 0 && i < 22 ? (mr() - 0.5) * 9 : 0;
      return [f1(lerp(xa, xb, u) + (i > 0 && i < 22 ? (mr() - 0.5) * 6 : 0)), f1(Math.max(0, hAt(u) * (0.78 + 0.22 * mr()) + jag))];
    });
    let chunks = "";
    for (let i = 0; i < 10; i++) {
      const u = 0.1 + mr() * 0.8;
      chunks += box(lerp(xa, xb, u), GROUND + 9 - hAt(u) * 0.86, 5 + mr() * 4, 2.6, (mr() - 0.5) * 1.6);
    }
    out.chunks = chunks;
    out.maxH = maxH;
    out.dust = Array.from({ length: 6 }, () => ({ u: 0.08 + mr() * 0.84, at: mr(), size: 24 + mr() * 30, drift: (mr() - 0.5) * 24 }));
  }
  return out;
}

const LOTS = [
  lot({
    ...FILLER, x0: FILLER.x, x1: FILLER.x + FILLER.w, top: GROUND - FILLER.h,
    polys: [boxPts(FILLER.x, GROUND - FILLER.h, FILLER.w, FILLER.h)], holes: FILLER_HOLES,
  }),
  ...SHOPS.map((s) => {
    const g = GEO[s.id];
    const polys = [facadePts(s)];
    if (s.cap === "cornice") polys.push(boxPts(s.x - 5, g.top - 3, s.w + 10, 8));
    return lot({
      ...s, x0: s.x - 5, x1: s.x + s.w + 5, top: g.top - 3, polys,
      holes: [hole(g.win), hole(g.door), ...g.ups.map((u) => hole(u))],
    });
  }),
  lot({
    ...THEATER, x0: THEATER.x, x1: THEATER.x + THEATER.w, top: GROUND - THEATER.h,
    polys: [facadePts(TH_CAP)],
    holes: [[THEATER.x + 22, MQ.y + MQ.h, THEATER.w - 44, GROUND - MQ.y - MQ.h + 4]],
  }),
  lot({
    ...BAR, x0: BAR.x - 3, x1: BAR.x + BAR.w + 3, top: GROUND - BAR.h - 3,
    polys: [boxPts(BAR.x, GROUND - BAR.h, BAR.w, BAR.h), boxPts(BAR.x - 3, GROUND - BAR.h - 3, BAR.w + 6, 7)],
    holes: [hole(BAR_WIN), hole(BAR_DOOR), ...BAR_UPS.map((u) => hole(u))],
  }),
  lot({
    ...CHURCH, x0: CHURCH.x, x1: CHURCH.x + CHURCH.w, top: 226,
    polys: [CHG.bodyPts, boxPts(CHG.cx - 15, 352, 30, 120), boxPts(CHG.cx - 19, 318, 38, 36),
      [[CHG.cx - 20, 320], [CHG.cx, 238], [CHG.cx + 20, 320]], boxPts(CHG.cx - 1, 226, 2, 12)],
    holes: [[CHG.cx - 14, 491, 28, 88], [CHURCH.x + 15, 507, 16, 60], [CHURCH.x + CHURCH.w - 31, 507, 16, 60],
      [CHG.cx - 12, 613, 24, 48], [CHG.cx - 9, 325, 18, 24]],
  }),
  lot({
    ...HOUSE, x0: HOUSE.x - 12, x1: HOUSE.x + HOUSE.w + 12, top: HOUSE.peak,
    polys: [boxPts(HOUSE.x, HOUSE.wall, HOUSE.w, GROUND - HOUSE.wall), ROOF_PTS, boxPts(1466, 488, 14, 40)],
    holes: [hole(HWIN), hole(HDOOR)],
  }),
];
const LOT = Object.fromEntries(LOTS.map((L) => [L.id, L]));

// Signs hang on the wall until the bricks behind them go, then drop into the
// heap and lie there at a tilt: rest is [centre x, centre y, degrees].
const MOUNTS = {
  diner: [{ kind: "sign", ...GEO.diner.sign, rest: [246, 642, -9] }],
  hardware: [{ kind: "sign", ...GEO.hardware.sign, rest: [404, 634, 11] }],
  barber: [
    { kind: "sign", ...GEO.barber.sign, rest: [566, 643, -13] },
    { kind: "pole", x: BARBER_POLE.x - 1.5, y: BARBER_POLE.y - 6, w: 13, h: 60, rest: [612, 652, 72] },
  ],
  feed: [{ kind: "sign", ...GEO.feed.sign, rest: [735, 641, 6] }],
  theater: [
    { kind: "blade", x: TH_BX - 17, y: 398, w: 34, h: 122, rest: [992, 641, 78] },
    { kind: "marquee", x: MQ.x - 4, y: MQ.y, w: MQ.w + 8, h: MQ.h, rest: [905, 631, -5] },
  ],
};

// Two scaffolds; each mason is [where along the deck, facing].
const SCAFFOLDS = [
  { lot: "hardware", masons: [[0.27, 1], [0.76, -1]], phase: 0 },
  { lot: "theater", masons: [[0.42, 1]], phase: 1.7 },
];

// ---------------------------------------------------------------------------
// Poles and lines. Wire A (the left power wire) is the one pulses ride; the
// town's line ends at the data center, the last pole feeding its roof.
// ---------------------------------------------------------------------------

const POLES = [-290, 20, 325, 640, 825, 1030, 1330];
const POLE_TOP = 314;
const ARM_Y = 326;
const PHONE_Y = 360;
const DC = { x: 1160, y: HORIZON, w: 500, h: 330, s: 0.72 };
const DC_TOP = DC.y - DC.h * DC.s;
const DC_FENCE = (() => {
  let d = `M -50 2 H ${DC.w + 50} M -50 14 H ${DC.w + 50} `;
  for (let x = -50; x <= DC.w + 50; x += 18) d += `M ${x} -6 V 26 `;
  return d;
})();
const FEED = { a: [1396, DC_TOP + 2], b: [1440, DC_TOP + 2] };
const LAMPS = [
  { x: 325, on: 0.19, off: 0.325, seed: 3 },
  { x: 640, on: 0.2, off: 0.39, seed: 5 },
  { x: 1030, on: 0.212, off: 0.475, seed: 7 },
  { x: 1330, on: 0.222, off: 0.54, seed: 11 },
];

const quad = (x1, y1, x2, y2, sag) => [x1, y1, (x1 + x2) / 2, (y1 + y2) / 2 + sag * 2, x2, y2];
const qAt = ([x0, y0, cx, cy, x1, y1], u) => {
  const v = 1 - u;
  return [v * v * x0 + 2 * v * u * cx + u * u * x1, v * v * y0 + 2 * v * u * cy + u * u * y1];
};
const qD = (segs) => segs
  .map(([x0, y0, cx, cy, x1, y1], i) => `${i ? "" : `M ${x0} ${y0} `}Q ${cx} ${cy} ${x1} ${y1}`)
  .join(" ");

// A run of quadratic spans with an arc-length table, so a pulse can ride it
// at a steady speed.
function track(segs) {
  const pts = [];
  segs.forEach((sg, i) => {
    for (let j = i ? 1 : 0; j <= 24; j++) pts.push(qAt(sg, j / 24));
  });
  const acc = [0];
  for (let i = 1; i < pts.length; i++) {
    acc.push(acc[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  const len = acc[acc.length - 1];
  const at = (s) => {
    const d = clamp(s, 0, len);
    let lo = 0;
    let hi = acc.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (acc[mid] < d) lo = mid;
      else hi = mid;
    }
    const k = (d - acc[lo]) / (acc[hi] - acc[lo] || 1);
    return [lerp(pts[lo][0], pts[hi][0], k), lerp(pts[lo][1], pts[hi][1], k)];
  };
  return { d: qD(segs), len, at };
}

const spans = (dx, y, sag) => POLES.slice(0, -1).map((px, i) => quad(px + dx, y, POLES[i + 1] + dx, y, sag));
const A_SPANS = spans(-20, ARM_Y, 20);
const FEED_A = quad(POLES[POLES.length - 1] - 20, ARM_Y, FEED.a[0], FEED.a[1], 6);
const FEED_B = quad(POLES[POLES.length - 1] + 20, ARM_Y, FEED.b[0], FEED.b[1], 8);
const WIRES = [
  qD([...A_SPANS, FEED_A]),
  qD([...spans(20, ARM_Y, 26), FEED_B]),
  qD([...spans(0, PHONE_Y, 30), quad(1330, PHONE_Y, 1700, PHONE_Y, 30)]),
];

// Service drops: where each building ties into the line. A building's light
// leaves down its own drop when it closes.
const DROPS = {
  diner: [292, 420], hardware: [470, 374], barber: [606, 428], feed: [788, 394],
  theater: [1000, 348], bar: [1128, 416], church: [1296, 452], house: [1356, 540],
};
const SOURCES = LOTS.filter((L) => DROPS[L.id]).map((L) => {
  const [ax, ay] = DROPS[L.id];
  let j = POLES.findIndex((px) => px - 20 > ax);
  if (j < 0) j = POLES.length - 1;
  const drop = quad(ax, ay, POLES[j] - 20, ARM_Y, 10);
  return {
    id: L.id, at: L.tear ? L.tear[0] : L.out, drop, dropD: qD([drop]), pole: [POLES[j] - 20, ARM_Y],
    row: Math.floor((GROUND - ay) / CH), track: track([drop, ...A_SPANS.slice(j), FEED_A]),
  };
});
const PULSE_SPEED = 470;
const PULSE_LEAD = 0.35; // seconds from a shop going dark to its pulse leaving

// Stars in four groups, each twinkling on its own beat, one path apiece.
// They shrink toward the horizon where the town's glow drowns them.
const STARS = (() => {
  const r = rng(505);
  const groups = Array.from({ length: 4 }, (_, g) => ({ d: "", ph: g * 1.7, rate: 0.5 + g * 0.37 }));
  for (let i = 0; i < 90; i++) {
    const x = -300 + r() * 2200;
    const y = -700 + r() * 1060;
    const size = 0.6 + r() * 1.1;
    const k = clamp((480 - y) / 300);
    if (k > 0.05) groups[i % 4].d += dot(x, y, +(size * (0.4 + 0.6 * k)).toFixed(2));
  }
  return groups;
})();

// A low line of trees on the far horizon.
const TREELINE = (() => {
  const r = rng(612);
  let d = `M -400 ${HORIZON + 4}`;
  for (let x = -400; x <= 2000; x += 12) {
    const h = 2 + r() * 6 + (Math.sin(x * 0.013) + 1) * 3.5;
    d += ` L ${x} ${f1(HORIZON - h)}`;
  }
  return d + ` L 2000 ${HORIZON + 4} Z`;
})();

// ---------------------------------------------------------------------------
// The screens: what fills the gaps. at is when each pops up, more when it
// gives up its pitch and just says MORE (fractions of the act).
// ---------------------------------------------------------------------------

const SCREENS = {
  // the big one, up on a monopole above the wires
  b1: { at: 0.6, more: 0.895, x: 598, y: 158, w: 300, h: 150, pole: 748, seed: 1, light: true },
  p1: { at: 0.64, more: 0.903, cx: 418, base: 652, w: 80, h: 156, tilt: -4, seed: 2 },
  p2: { at: 0.665, more: 0.909, cx: 574, base: 650, w: 62, h: 122, tilt: 5, seed: 3, light: true },
  b2: { at: 0.69, more: 0.915, x: 168, y: 400, w: 150, h: 84, legs: [196, 290], seed: 4 },
  w1: { at: 0.715, more: 0.92, x: BAR_WIN.x, y: BAR_WIN.y, w: BAR_WIN.w, h: BAR_WIN.h, seed: 5 },
  t1: { at: 0.735, more: 0.925, x: TH_BX - 17, y: 404, w: 34, h: 148, seed: 6 },
  p3: { at: 0.755, more: 0.93, cx: 1374, base: 654, w: 66, h: 128, tilt: -3, seed: 7 },
};

// The swarm: phones standing in the rubble, more of them every second. The
// gaps between arrivals shrink, so they seem to breed.
const SWARM = (() => {
  const r = rng(909);
  const spots = [
    [672, 650, 28], [345, 650, 24], [790, 650, 26], [480, 652, 22], [625, 652, 24], [1432, 650, 26],
    [712, 636, 18], [520, 650, 20], [870, 652, 20], [985, 650, 22], [312, 652, 16], [1488, 652, 20],
    [500, 630, 13], [812, 630, 13], [690, 626, 12], [512, 628, 12], [1462, 630, 15], [365, 628, 12],
  ];
  return spots.map(([cx, base, w], i) => ({
    cx, base, w, h: w * 1.95, tilt: (r() - 0.5) * 14, light: r() > 0.55,
    at: 0.775 + 0.125 * Math.sqrt((i + r() * 0.6) / spots.length), seed: 20 + i,
  }));
})();
// Tiny screens far off on the horizon, seen through the gaps the shops left.
const FAR = (() => {
  const r = rng(911);
  const gaps = [[330, 500], [505, 640], [660, 820], [1340, 1510]];
  return Array.from({ length: 30 }, (_, i) => {
    const [a, b] = gaps[i % gaps.length];
    const w = 5 + r() * 8;
    return {
      x: a + r() * (b - a - w), y: HORIZON - 2 - w * 1.6 - r() * 26, w, h: w * 1.6,
      at: 0.8 + 0.12 * Math.sqrt(i / 30), g: i % 3,
    };
  });
})();
const ODDS = ["+250", "+400", "+650", "+900"];
const SPARK = (() => {
  const r = rng(313);
  let v = 0.1;
  return Array.from({ length: 12 }, (_, i) => {
    v = clamp(v + (r() - 0.3) * 0.22, 0.02, 1);
    return [i / 11, i === 11 ? 1 : v];
  });
})();
const PITCH = 17; // slot reel symbol spacing
const SYM = [
  (x, y) => R(x - 6, y - 2.6, 12, 5.2),
  (x, y) => `M ${x} ${f1(y - 6.2)} L ${x + 5.4} ${f1(y)} L ${x} ${f1(y + 6.2)} L ${x - 5.4} ${f1(y)} Z `,
  (x, y) => dot(x, y, 5.2) + dot(x, y, 2.3),
  (x, y) => `M ${x - 5} ${f1(y - 6)} H ${x + 5} V ${f1(y - 3.4)} L ${x - 0.4} ${f1(y + 6)} H ${x - 3.6} L ${x + 1.6} ${f1(y - 3.4)} H ${x - 5} Z `,
];
const TICKER = "ODDS BOOST  ·  PARLAY +900  ·  FREE PLAY  ·  SPIN TO WIN  ·  BET NOW  ·  ";
const TICKER_MORE = "MORE  ·  MORE  ·  MORE  ·  MORE  ·  ";

// ---------------------------------------------------------------------------
// The state of one lot at this moment: how much wall stands, the course
// being laid, the bricks still clinging to a course that's coming down, the
// bricks in the air, and how big the heap is.
// ---------------------------------------------------------------------------

const brickD = (row, b) => R(b.x0, row.y0, b.x1 - b.x0, row.y1 - row.y0);

function tearTimes(L, dur) {
  const tOut = L.tear[0] * dur;
  const rows = Math.max(1, L.n - L.remRow);
  const D = clamp(rows * 0.07, 1.2, 2.4);
  return { tOut, tFall: tOut + 0.6, rowDur: D / rows, D };
}

function lotState(L, p, local, dur) {
  const u = seg(p, L.build[0], L.build[1]);
  // the time-lapse picks up speed as it goes
  const laid = L.n * (0.4 * u + 0.6 * u * u);
  const built = u >= 1 ? L.n : Math.floor(laid);
  let cut = built;
  const tDone = L.build[1] * dur;
  const tOut = (L.tear ? L.tear[0] : L.out) * dur;
  const lit = kindle(local, tDone + 0.1, L.seed) * failing(local, tOut, L.seed);
  let fresh = "";
  if (u > 0 && u < 1 && built < L.n) {
    const row = L.rows[built];
    const m = Math.floor((laid - built) * row.bricks.length);
    for (let i = 0; i < m; i++) fresh += brickD(row, row.bricks[i]);
  }
  let attached = "";
  let falling = "";
  let fallen = 0;
  let T = null;
  if (L.tear) {
    T = tearTimes(L, dur);
    if (local > T.tFall) {
      const started = Math.min(L.n - L.remRow, Math.floor((local - T.tFall) / T.rowDur) + 1);
      cut = L.n - started;
      for (let r = cut; r < L.n; r++) {
        const row = L.rows[r];
        const tr = T.tFall + (L.n - 1 - r) * T.rowDur;
        for (const b of row.bricks) {
          const dt = b.stay ? -1 : local - (tr + b.ord * T.rowDur * 1.8);
          if (dt < 0) {
            attached += brickD(row, b);
            continue;
          }
          if (!b.out) continue;
          const y = (row.y0 + row.y1) / 2 + b.vy * dt + 0.5 * GRAV * dt * dt;
          if (y < GROUND + 14) falling += box((b.x0 + b.x1) / 2 + b.vx * dt, y, (b.x1 - b.x0) / 2, 4, b.spin * dt);
        }
      }
      fallen = smooth(clamp((local - T.tFall - 0.2) / (T.D + 0.5)));
    }
  }
  const freshK = u <= 0 ? 0 : u < 1 ? 1 : 1 - seg(local, tDone, tDone + 0.8);
  return { u, built, cut, hgt: cut * CH, lit, fresh, attached, falling, fallen, freshK, T, tDone, tOut };
}

// Where a sign is now: on the wall, falling, or lying in the heap. Returns
// null while the wall hasn't reached it yet, "" while it hangs in place.
function mountTf(m, L, S, local) {
  if (S.built * CH < GROUND - m.y - CH) return null;
  const row = Math.floor((GROUND - (m.y + m.h / 2)) / CH);
  if (!S.T || row < L.remRow) return "";
  const d = local - (S.T.tFall + (L.n - 1 - row) * S.T.rowDur);
  if (d <= 0) return "";
  const k = clamp(d / 0.6);
  const cx0 = m.x + m.w / 2;
  const cy0 = m.y + m.h / 2;
  const [rx, ry, ra] = m.rest;
  const settle = d > 0.6 ? 3 * Math.exp(-(d - 0.6) * 7) * Math.sin((d - 0.6) * 26) : 0;
  const cx = lerp(cx0, rx, smooth(k));
  const cy = lerp(cy0, ry, k * k);
  return `translate(${f1(cx)} ${f1(cy)}) rotate(${f1(ra * smooth(k) + settle)}) translate(${-cx0} ${-cy0})`;
}

// A screen switching on: it pops up out of nothing, overshoots, flashes and
// settles to a cold glow with the odd stutter.
function popState(local, at, seed, moreAt) {
  const d = local - at;
  if (d <= 0) return null;
  const k = clamp(d / 0.38);
  const boot = d < 0.5 ? (hash(Math.floor(d * 22) + seed * 7) > 0.3 ? 1 : 0.35) : 1;
  const stutter = hash(Math.floor(local * 9) + seed * 31) > 0.95 ? 0.55 : 1;
  const sw = local - moreAt;
  return {
    scale: 0.45 + 0.55 * backOut(k),
    op: clamp(d / 0.12),
    flash: Math.max(clamp(1 - d / 0.45), sw > 0 ? clamp(1 - sw / 0.3) * 0.8 : 0),
    bright: boot * stutter,
    more: sw > 0,
  };
}

// ---------------------------------------------------------------------------
// Small parts
// ---------------------------------------------------------------------------

function Txt({ x, y, size, color, children, anchor = "middle", spacing = 0.16, weight = 500, opacity = 1, glow = 0 }) {
  const common = {
    x, y, fontFamily: FONT.mono, fontSize: size, fontWeight: weight,
    letterSpacing: `${spacing}em`, textAnchor: anchor,
  };
  return (
    <>
      {glow > 0.02 && (
        <text {...common} fill="none" stroke={C.warm} strokeWidth={size * 0.4} strokeLinejoin="round" opacity={glow * 0.22}>
          {children}
        </text>
      )}
      <text {...common} fill={color} opacity={opacity}>{children}</text>
    </>
  );
}

// Screen copy: mono, bold, centred by default (letter-spacing nudged back).
function ST({ x, y, s, c, a = "middle", w = 700, sp = 0.06, o = 1, children }) {
  return (
    <text
      x={a === "middle" ? f1(x + (s * sp) / 2) : x} y={y} fill={c} opacity={o}
      fontFamily={FONT.mono} fontSize={s} fontWeight={w} letterSpacing={`${sp}em`} textAnchor={a}
    >
      {children}
    </text>
  );
}

// A reflection on the wet road: a broken streak of carved strokes under a light.
const STREAKS = new Map();
function streak(cx, w, seed) {
  const key = `${cx} ${w} ${seed}`;
  if (STREAKS.has(key)) return STREAKS.get(key);
  let d = "";
  for (let i = 0; i < 12; i++) {
    const r1 = hash(seed * 7.1 + i * 1.7);
    const r2 = hash(seed * 3.3 + i * 2.9);
    const ww = w * (1 - i * 0.06) * (0.3 + 0.7 * r1);
    const x0 = cx - ww / 2 + (r2 - 0.5) * w * 0.35;
    d += R(+x0.toFixed(1), CURB + 5 + i * 9 + Math.round(r2 * 4), +ww.toFixed(1), 2.4);
  }
  STREAKS.set(key, d);
  return d;
}

// Warm spill onto the sidewalk and its streak on the wet street.
function Spill({ x, w, k, seed = 1 }) {
  if (k <= 0.01) return null;
  return (
    <>
      <ellipse cx={x + w / 2} cy={GROUND + 15} rx={w * 0.7 + 24} ry={22} fill="url(#ms-pool)" opacity={k} />
      <path d={streak(x + w / 2, w * 0.6, seed)} fill="url(#ms-refl)" opacity={k * 0.6} />
    </>
  );
}

function UpWindow({ r, k, cold }) {
  return (
    <>
      <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={mixColor(INK.glass, INK.glassCold, cold)} />
      {k > 0.01 && <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="url(#ms-up)" opacity={k} />}
      <path d={`M ${r.x} ${r.y + r.h * 0.5} h ${r.w} M ${r.x} ${r.y + 1} h ${r.w}`} stroke="#0a0a0b" strokeWidth={2} />
    </>
  );
}

// ---------------------------------------------------------------------------
// The fronts. Each draws its wall (clipped to however much of it stands);
// signs are drawn apart so they can fall.
// ---------------------------------------------------------------------------

function ShopWall({ s, lit, face, cold }) {
  const g = GEO[s.id];
  const { win, door } = g;
  const glass = mixColor(INK.glass, INK.glassCold, cold);
  return (
    <>
      <path d={g.face} fill={face} />
      <path d={g.face} fill="url(#ms-brick)" />
      <path d={g.rim} fill="none" stroke={mixColor(INK.rim, INK.rimCold, cold)} strokeWidth={1.5} opacity={0.55} />
      {s.cap === "cornice" && (
        <path d={R(s.x - 5, g.top - 3, s.w + 10, 8) + R(s.x - 2, g.top + 5, s.w + 4, 3)} fill={face} />
      )}
      {g.ups.map((r, i) => <UpWindow key={i} r={r} k={s.ups[i] * lit * 0.85} cold={cold} />)}
      <rect x={win.x} y={win.y} width={win.w} height={win.h} fill={glass} />
      {lit > 0.01 && <rect x={win.x} y={win.y} width={win.w} height={win.h} fill="url(#ms-lit)" opacity={lit} />}
      <path d={g.interior} fill={INK.sil} opacity={0.3 + 0.55 * lit} />
      {s.id === "diner" && lit > 0.01 && (
        <path
          d={[0.2, 0.5, 0.8].map((u) => R(win.x + win.w * u - 4, win.y + win.h * 0.2, 8, 2)).join(" ")}
          fill={C.warmHot}
          opacity={lit}
        />
      )}
      <rect x={win.x} y={win.y} width={win.w} height={win.h} fill="none" stroke="#090909" strokeWidth={3} />
      <rect x={door.x} y={door.y} width={door.w} height={door.h} fill="#0a0a0b" />
      <rect x={door.x + 5} y={door.y + 8} width={door.w - 10} height={42} fill={glass} />
      {lit > 0.01 && <rect x={door.x + 5} y={door.y + 8} width={door.w - 10} height={42} fill="url(#ms-lit)" opacity={lit * 0.8} />}
    </>
  );
}

function ShopSign({ s, lit }) {
  const { sign } = GEO[s.id];
  return (
    <>
      <rect x={sign.x} y={sign.y} width={sign.w} height={sign.h} fill="#0d0d0e" />
      <Txt
        x={sign.x + sign.w / 2} y={sign.y + sign.h / 2 + s.size * 0.36} size={s.size}
        color={mixColor(INK.signOff, C.warmHot, lit)} glow={lit}
      >
        {s.sign}
      </Txt>
    </>
  );
}

function BarberPole({ k, local }) {
  const { x, y } = BARBER_POLE;
  const shift = (local * 16) % 12;
  let stripes = "";
  for (let i = -2; i < 7; i++) stripes += `M ${x} ${f1(y + i * 12 + shift)} l 10 -7 v 5 l -10 7 Z `;
  return (
    <g>
      <defs>
        <clipPath id="ms-pole">
          <rect x={x} y={y} width={10} height={48} rx={4} />
        </clipPath>
      </defs>
      <rect x={x} y={y} width={10} height={48} rx={4} fill={mixColor("#1a1c1f", "#f4dcb4", k)} />
      <path d={stripes} fill={mixColor("#111214", C.warmDeep, k)} clipPath="url(#ms-pole)" />
      <rect x={x - 1.5} y={y - 6} width={13} height={6} rx={3} fill="#0b0b0c" />
      <rect x={x - 1.5} y={y + 48} width={13} height={6} rx={3} fill="#0b0b0c" />
    </g>
  );
}

function TheaterWall({ lit, face, cold }) {
  const { x, w } = THEATER;
  const glass = mixColor(INK.glass, INK.glassCold, cold);
  return (
    <>
      <path d={TH_FACE} fill={face} />
      <path d={TH_FACE} fill="url(#ms-brick)" />
      <path d={TH_RIM} fill="none" stroke={mixColor(INK.rim, INK.rimCold, cold)} strokeWidth={1.5} opacity={0.55} />
      <path d={`M ${x + 14} ${GROUND - THEATER.h + 30} V 520 M ${x + w - 14} ${GROUND - THEATER.h + 30} V 520`} stroke="#1c1a19" strokeWidth={3} />
      {/* entrance: recess, booth, poster cases */}
      <rect x={x + 22} y={MQ.y + MQ.h} width={w - 44} height={GROUND - MQ.y - MQ.h} fill="#08090a" />
      <path d={arch(TH_BX - 15, 588, 30, 72)} fill="#121212" />
      <rect x={TH_BX - 10} y={600} width={20} height={16} fill={glass} />
      {lit > 0.01 && <rect x={TH_BX - 10} y={600} width={20} height={16} fill="url(#ms-lit)" opacity={lit} />}
      {[x + 26, x + w - 50].map((px, i) => (
        <React.Fragment key={i}>
          <rect x={px} y={588} width={24} height={40} fill={glass} stroke="#111" strokeWidth={2} />
          {lit > 0.01 && <rect x={px + 2} y={590} width={20} height={36} fill="url(#ms-up)" opacity={lit * 0.9} />}
        </React.Fragment>
      ))}
    </>
  );
}

function Blade({ lit }) {
  return (
    <>
      <rect x={TH_BX - 17} y={398} width={34} height={122} fill="#0c0c0d" stroke={mixColor("#1e1f22", C.warm, lit)} strokeWidth={2} />
      {"THEATER".split("").map((ch, i) => (
        <Txt key={i} x={TH_BX} y={418 + i * 16.2} size={15} weight={700} spacing={0} color={mixColor(INK.signOff, C.warmHot, lit)} glow={lit}>
          {ch}
        </Txt>
      ))}
    </>
  );
}

function Marquee({ lit, local }) {
  const chase = Math.floor(local * 7);
  return (
    <>
      <rect x={MQ.x - 4} y={MQ.y} width={MQ.w + 8} height={MQ.h} fill="#0e0e0f" />
      <rect x={MQ.x + 6} y={MQ.y + 9} width={MQ.w - 12} height={MQ.h - 18} fill={mixColor("#3a434d", "#f3d6a8", lit)} opacity={0.35 + 0.65 * lit} />
      {lit > 0.02 && <Txt x={TH_BX} y={MQ.y + 26} size={12} weight={700} color="#24170c" opacity={lit}>NOW SHOWING</Txt>}
      <g transform={`translate(${MQ.x} ${MQ.y})`}>
        {BULBS.map((d, c) => (
          <path key={c} d={d} fill={mixColor("#26221e", C.warmHot, lit * ((c + chase) % 3 === 0 ? 0.35 : 1))} />
        ))}
      </g>
    </>
  );
}

function BarWall({ lit, local, face, cold }) {
  const { x, w } = BAR;
  // Neon buzz: the odd half-second stutter while it's still on.
  const buzz = hash(Math.floor(local * 10) + 91) > 0.93 ? 0.45 : 1;
  const neon = lit * (lit >= 1 ? buzz : 1);
  const glass = mixColor(INK.glass, INK.glassCold, cold);
  const top = GROUND - BAR.h;
  const win = BAR_WIN;
  return (
    <>
      <path d={BAR_FACE} fill={face} />
      <path d={BAR_FACE} fill="url(#ms-brick)" />
      <path d={R(x - 3, top - 3, w + 6, 7)} fill={face} />
      <path d={`M ${x - 3} ${top - 3} h ${w + 6}`} stroke={mixColor(INK.rim, INK.rimCold, cold)} strokeWidth={1.5} opacity={0.55} />
      {BAR_UPS.map((r, i) => <UpWindow key={i} r={r} k={0.85 * lit} cold={cold} />)}
      <rect x={win.x} y={win.y} width={win.w} height={win.h} fill={glass} />
      {lit > 0.01 && <rect x={win.x} y={win.y} width={win.w} height={win.h} fill="url(#ms-lit)" opacity={lit * 0.55} />}
      <path
        d={R(win.x, win.y + 50, win.w, 2.5) + Array.from({ length: 9 }, (_, i) => R(win.x + 5 + i * 7, win.y + 38 - (i % 3) * 2, 3.5, 12 + (i % 3) * 2)).join(" ")}
        fill={INK.sil}
        opacity={0.35 + 0.65 * lit}
      />
      <Txt x={win.x + win.w / 2} y={win.y + 30} size={21} weight={700} spacing={0.12} color={mixColor("#2a2522", C.warmHot, neon)} glow={neon * 1.6}>
        BAR
      </Txt>
      <rect x={win.x} y={win.y} width={win.w} height={win.h} fill="none" stroke="#090909" strokeWidth={3} />
      <rect x={BAR_DOOR.x} y={BAR_DOOR.y} width={BAR_DOOR.w} height={BAR_DOOR.h} fill="#0a0a0b" />
      <rect x={x + w - 38} y={558} width={18} height={24} fill={glass} />
      {lit > 0.01 && <rect x={x + w - 38} y={558} width={18} height={24} fill="url(#ms-lit)" opacity={lit * 0.7} />}
    </>
  );
}

function ChurchWall({ lit, face, cold }) {
  const glass = mixColor(INK.glass, INK.glassCold, cold);
  return (
    <>
      <path d={CHG.body} fill={face} />
      <path d={CHG.tower} fill={face} />
      <path d={CHG.body} fill="url(#ms-brick)" opacity={0.6} />
      <path d={CHG.rim} fill="none" stroke={mixColor(INK.rim, INK.rimCold, Math.max(cold, 0.5))} strokeWidth={1.5} opacity={0.6} />
      <path d={CHG.belfry} fill="url(#ms-belfry)" opacity={0.35 + 0.4 * cold} />
      <path d={CHG.louvre} stroke={face} strokeWidth={2.2} />
      <path d={CHG.main + " " + CHG.side} fill={glass} />
      {lit > 0.01 && <path d={CHG.main + " " + CHG.side} fill="url(#ms-lit)" opacity={lit * 0.9} />}
      <path d={CHG.lead} stroke="#120d0a" strokeWidth={1.6} opacity={0.4 + 0.6 * lit} />
      <path d={CHG.door} fill="#0a0a0b" />
      <path d={R(CHURCH.x + CHURCH.w / 2 - 20, GROUND - 6, 40, 6)} fill="#121214" />
    </>
  );
}

function HouseWall({ lit, local, face, cold }) {
  const wall = mixColor("#171311", INK.facadeCold, cold * 0.8);
  const rim = mixColor(INK.rim, INK.rimCold, Math.max(cold, 0.4));
  const sway = 3 * Math.sin(local * 1.15);
  const { x, y, w, h } = HWIN;
  return (
    <>
      <path d={R(1466, 488, 14, 40)} fill={face} />
      <path d={ROOF + " Z"} fill="#0c0c0d" />
      <path d={ROOF} fill="none" stroke={rim} strokeWidth={1.6} opacity={0.7} />
      <rect x={HOUSE.x} y={HOUSE.wall} width={HOUSE.w} height={GROUND - HOUSE.wall} fill={wall} />
      <path d={CLAPBOARD} stroke="#221c18" strokeWidth={1} opacity={0.7} />
      <rect x={x} y={y} width={w} height={h} fill={mixColor("#120c08", INK.glassCold, cold)} />
      {lit > 0.01 && <rect x={x} y={y} width={w} height={h} fill="url(#ms-lit)" opacity={lit * 0.85} />}
      {lit > 0.01 && <circle cx={x + w * 0.42 + sway} cy={y + 26} r={30} fill="url(#ms-halo)" opacity={lit * 0.8} />}
      {/* curtains, the pull-cord bulb, the sash */}
      <path
        d={`M ${x} ${y} h 16 q -5 40 -1 ${h} h -15 Z M ${x + w} ${y} h -16 q 5 40 1 ${h} h 15 Z`}
        fill="#160e09" opacity={0.6 + 0.4 * lit}
      />
      <path d={`M ${x + w * 0.42} ${y} L ${x + w * 0.42 + sway} ${y + 22}`} stroke="#140d08" strokeWidth={0.9} />
      <rect x={x} y={y} width={w} height={h} fill="none" stroke="#0b0908" strokeWidth={3} />
      <path d={R(x + w / 2 - 1.5, y, 3, h) + R(x, y + h / 2 - 1.5, w, 3)} fill="#0b0908" />
      <rect x={x - 5} y={y + h} width={w + 10} height={5} fill="#0e0c0b" />
      <rect x={HDOOR.x} y={HDOOR.y} width={HDOOR.w} height={HDOOR.h} fill="#0b0a0a" />
      <rect x={HDOOR.x - 4} y={GROUND - 5} width={HDOOR.w + 8} height={5} fill="#141414" />
    </>
  );
}

function FillerWall({ face, cold }) {
  return (
    <>
      <path d={FILLER_FACE} fill={face} />
      <path d={FILLER_FACE} fill="url(#ms-brick)" />
      <path d={FILLER_GLASS} fill={mixColor(INK.glass, INK.glassCold, cold)} />
    </>
  );
}

function Wall({ L, S, face, cold, local }) {
  const lit = S.lit;
  switch (L.id) {
    case "theater":
      return <TheaterWall lit={lit} face={face} cold={cold} />;
    case "bar":
      return <BarWall lit={lit} local={local} face={face} cold={cold} />;
    case "church":
      return <ChurchWall lit={lit} face={face} cold={cold} />;
    case "house":
      return <HouseWall lit={lit} local={local} face={face} cold={cold} />;
    case "filler":
      return <FillerWall face={face} cold={cold} />;
    default:
      return <ShopWall s={L} lit={lit} face={face} cold={cold} />;
  }
}

// One lot: the standing wall clipped to its height, the warm new courses at
// the top while it goes up, and any bricks hanging on in a course that's
// coming down.
function Lot({ L, S, face, cold, local }) {
  if (S.hgt <= 0 && !S.attached && !S.fresh) return null;
  const top = GROUND - S.hgt;
  return (
    <g>
      {S.hgt > 0 && (
        <>
          <defs>
            <clipPath id={`ms-cl-${L.id}`}>
              <rect x={L.x0 - 12} y={top} width={L.x1 - L.x0 + 24} height={S.hgt + 40} />
            </clipPath>
            {S.freshK > 0 && (
              <linearGradient id={`ms-fr-${L.id}`} gradientUnits="userSpaceOnUse" x1="0" y1={top} x2="0" y2={top + 60}>
                <stop offset="0" stopColor={INK.fresh} stopOpacity="0.9" />
                <stop offset="0.35" stopColor={INK.fresh} stopOpacity="0.35" />
                <stop offset="1" stopColor={INK.fresh} stopOpacity="0" />
              </linearGradient>
            )}
          </defs>
          <g clipPath={`url(#ms-cl-${L.id})`}>
            <Wall L={L} S={S} face={face} cold={cold} local={local} />
            {S.freshK > 0 && <path d={L.faceD} fill={`url(#ms-fr-${L.id})`} opacity={S.freshK} />}
          </g>
        </>
      )}
      {S.attached && <path d={S.attached} fill={face} />}
      {S.fresh && <path d={S.fresh} fill={INK.fresh} />}
    </g>
  );
}

function Mount({ L, m, S, tf, local }) {
  const body = (() => {
    switch (m.kind) {
      case "pole":
        return <BarberPole k={S.lit} local={Math.min(local, S.tOut + 0.4)} />;
      case "blade":
        return <Blade lit={S.lit} />;
      case "marquee":
        return <Marquee lit={S.lit} local={local} />;
      default:
        return <ShopSign s={L} lit={S.lit} />;
    }
  })();
  return tf ? <g transform={tf}>{body}</g> : body;
}

// Stakes and a string line on an empty lot.
function Footing({ L, S }) {
  const k = 1 - clamp(S.hgt / 36);
  if (k <= 0.01 || L.id === "filler") return null;
  const a = L.x + 3;
  const b = L.x + L.w - 3;
  return (
    <g opacity={k}>
      <rect x={a} y={GROUND - 4} width={b - a} height={4} fill="#0c0a09" />
      <path d={`M ${a} ${GROUND} V ${GROUND - 20} M ${b} ${GROUND} V ${GROUND - 20}`} stroke="#0c0a09" strokeWidth={2.5} />
      <path d={`M ${a} ${GROUND - 16} H ${b}`} stroke="#a08566" strokeWidth={0.8} opacity={0.45} />
    </g>
  );
}

function Scaffold({ L, S, sc, local }) {
  const k = S.u <= 0 ? 0 : S.u < 1 ? 1 : 1 - seg(local, S.tDone, S.tDone + 0.5);
  if (k <= 0.01) return null;
  const x0 = L.x + 8;
  const x1 = L.x + L.w - 8;
  const xm = (x0 + x1) / 2;
  const wallTop = GROUND - S.hgt - (S.fresh ? CH : 0);
  const deck = Math.min(GROUND, wallTop + 40);
  const rail = deck - 30;
  let frame = `M ${x0} ${GROUND} V ${rail} M ${xm} ${GROUND} V ${rail} M ${x1} ${GROUND} V ${rail} M ${x0 - 5} ${rail} H ${x1 + 5}`;
  let brace = "";
  for (let y = GROUND; y > deck + 4; y -= 36) {
    const y2 = Math.max(deck, y - 36);
    frame += ` M ${x0 - 5} ${y2} H ${x1 + 5}`;
    brace += ` M ${x0} ${y} L ${xm} ${y2} M ${xm} ${y} L ${x1} ${y2}`;
  }
  return (
    <g opacity={k}>
      <path d={frame} stroke={INK.ink} strokeWidth={2.4} fill="none" />
      {brace && <path d={brace} stroke={INK.ink} strokeWidth={1.2} fill="none" />}
      <rect x={x0 - 8} y={deck} width={x1 - x0 + 16} height={4.5} fill={INK.ink} />
      <path d={R(x0 + 2, deck - 9, 16, 9) + R(x1 - 18, deck - 6, 14, 6)} fill={INK.ink} />
      {sc.masons.map(([u, facing], i) => (
        <Worker
          key={i} x={lerp(x0, x1, u)} y={deck} s={0.4} facing={facing} pose="work"
          phase={local * 6.5 + sc.phase + i * 2.1} color={INK.ink}
        />
      ))}
    </g>
  );
}

function Rubble({ L, F, scr }) {
  if (F <= 0.001) return null;
  const pts = L.mound.map(([x, h]) => [x, f1(GROUND + 6 - h * F)]);
  const top = poly(pts);
  const last = pts[pts.length - 1][0];
  return (
    <g>
      <path d={`${top} L ${last} ${GROUND + 10} L ${pts[0][0]} ${GROUND + 10} Z`} fill={INK.rubble} />
      <path d={top} fill="none" stroke={mixColor("#3b2b21", "#7d9cba", scr)} strokeWidth={1.2} opacity={0.3 + 0.25 * scr} />
      {F > 0.6 && (
        <path
          d={L.chunks} fill={mixColor("#2a1f19", "#2b3a4a", scr)} opacity={seg(F, 0.6, 1)}
          transform={`translate(0 ${f1((1 - F) * L.maxH)})`}
        />
      )}
    </g>
  );
}

function Dust({ L, S, local }) {
  if (!S.T || local < S.T.tFall) return null;
  const { T } = S;
  const out = [];
  L.dust.forEach((d, i) => {
    const age = local - (T.tFall + 0.35 + d.at * T.D);
    if (age <= 0 || age >= 2.6) return;
    const k = age / 2.6;
    const rx = d.size * (0.45 + 0.9 * Math.sqrt(k));
    const o = 0.55 * (k < 0.12 ? k / 0.12 : (1 - k) / 0.88);
    out.push(
      <ellipse key={i} cx={f1(L.x + d.u * L.w + d.drift * k)} cy={f1(GROUND - 8 - 30 * k)} rx={f1(rx)} ry={f1(rx * 0.5)} fill="url(#ms-dust)" opacity={o} />
    );
  });
  // a little grit coming off the broken top while the courses go
  const going = local < T.tFall + T.D + 0.4;
  if (going && S.hgt > 0) {
    out.push(<ellipse key="top" cx={L.x + L.w / 2} cy={GROUND - S.hgt} rx={L.w * 0.42} ry={12} fill="url(#ms-dust)" opacity={0.35} />);
  }
  return out;
}

function DropLine({ src, S, local }) {
  const shown = seg(local, S.tDone + 0.1, S.tDone + 0.6);
  if (shown <= 0) return null;
  if (!(S.T && S.cut <= src.row)) {
    return <Draw d={src.dropD} p={shown} stroke="#0c0c0d" strokeWidth={1.1} />;
  }
  // the building it fed is gone: the drop hangs off the pole
  const [px, py] = src.pole;
  const ex = px + (src.drop[0] - px) * 0.2 + 5 * Math.sin(local * 1.3 + src.row);
  const ey = py + 92;
  return <path d={`M ${px} ${py} Q ${f1(px + (ex - px) * 0.25)} ${ey - 8} ${f1(ex)} ${ey}`} stroke="#0c0c0d" strokeWidth={1.1} fill="none" />;
}

function LampLight({ x, k }) {
  if (k <= 0.01) return null;
  const hx = x + 50;
  const hy = 410;
  return (
    <>
      <path d={poly([[hx - 6, hy + 3], [hx + 6, hy + 3], [hx + 84, CURB + 6], [hx - 84, CURB + 6]]) + " Z"} fill="url(#ms-cone)" opacity={k * 0.6} />
      <ellipse cx={hx} cy={CURB + 2} rx={100} ry={13} fill="url(#ms-pool)" opacity={k * 0.75} />
      <path d={streak(hx, 16, x)} fill="url(#ms-refl)" opacity={k * 0.7} />
      <circle cx={hx} cy={hy + 3} r={30} fill="url(#ms-halo)" opacity={k * 0.85} />
    </>
  );
}

function Pole({ x, lamp }) {
  const hx = x + 50;
  const hy = 410;
  return (
    <g fill="#0a0a0b" stroke="#0a0a0b">
      <path d={`M ${x} ${POLE_TOP} V ${CURB}`} strokeWidth={6} />
      <path d={`M ${x - 26} ${ARM_Y + 3} H ${x + 26} M ${x - 6} ${PHONE_Y + 2} H ${x + 6}`} strokeWidth={5} />
      <path d={R(x - 21.5, ARM_Y - 6, 3, 6) + R(x + 18.5, ARM_Y - 6, 3, 6)} stroke="none" />
      {x === 1030 && <rect x={x + 5} y={ARM_Y + 16} width={13} height={24} rx={3} stroke="none" />}
      {lamp && (
        <>
          <path d={`M ${x} ${hy + 16} Q ${x + 6} ${hy - 2} ${x + 30} ${hy - 1} L ${hx - 6} ${hy}`} fill="none" strokeWidth={3} />
          <path d={`M ${hx - 11} ${hy + 3} Q ${hx} ${hy - 6} ${hx + 11} ${hy + 2} Z`} stroke="none" />
        </>
      )}
    </g>
  );
}

// Light leaving a shop: it rides the drop up to the line and the line into
// the data center, warm when it leaves and cold when it gets there. The whole
// route glows faintly while it's in flight.
function Pulse({ tr, pos }) {
  const [x, y] = tr.at(pos);
  const k = pos / tr.len;
  const col = mixColor(C.warmHot, C.coldHot, smooth(k));
  const tail = Math.min(150, pos) / tr.len;
  return (
    <g>
      <path d={tr.d} fill="none" stroke={col} strokeWidth={1.6} opacity={0.16 * Math.sin(Math.PI * k)} />
      <path
        d={tr.d} pathLength={1} fill="none" stroke={col} strokeWidth={2.6} strokeLinecap="round"
        strokeDasharray={`${tail} 2`} strokeDashoffset={tail - k} opacity={0.85}
      />
      <circle cx={x} cy={y} r={28} fill="url(#ms-pw)" opacity={1 - k} />
      <circle cx={x} cy={y} r={28} fill="url(#ms-pc)" opacity={k} />
      <circle cx={x} cy={y} r={3.2} fill={col} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

const SCR_INK = "#06101a";

// A billboard: on two legs, or up on a monopole with a catwalk under it.
function Board({ s, st, children }) {
  const { x, y, w, h } = s;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const legs = s.pole
    ? R(s.pole - 8, y + h + 4, 16, GROUND + 4 - y - h) + R(x - 4, y + h + 6, w + 8, 3.5)
    : s.legs.map((lx) => R(lx - 3, y + h, 6, GROUND + 4 - y - h)).join(" ");
  const brace = s.pole
    ? `M ${s.pole - 7} ${y + h + 44} L ${x + 30} ${y + h + 8} M ${s.pole + 7} ${y + h + 44} L ${x + w - 30} ${y + h + 8}`
    : `M ${s.legs[0]} ${y + h + 26} L ${s.legs[1]} ${y + h + 76} M ${s.legs[1]} ${y + h + 26} L ${s.legs[0]} ${y + h + 76}`;
  return (
    <g>
      <g opacity={st.op}>
        <ellipse cx={cx} cy={cy} rx={w * 0.95} ry={h * 1.15} fill="url(#ms-sglow)" opacity={0.55 * st.bright} />
        <path d={legs} fill="#06080b" />
        <path d={brace} stroke="#06080b" strokeWidth={2.2} fill="none" />
      </g>
      <g transform={`translate(${cx} ${cy}) scale(${f1(st.scale * 100) / 100}) translate(${-cx} ${-cy})`} opacity={st.op}>
        <rect x={x - 6} y={y - 6} width={w + 12} height={h + 12} rx={2} fill="#04060a" />
        <rect x={x} y={y} width={w} height={h} fill={`url(#ms-scr-${s.light ? "l" : "d"})`} opacity={st.bright} />
        <g opacity={st.bright}>{children}</g>
        <rect x={x} y={y} width={w} height={h} fill="url(#ms-scan)" />
        {st.flash > 0.01 && <rect x={x} y={y} width={w} height={h} fill="#f2f9ff" opacity={st.flash} />}
      </g>
    </g>
  );
}

function Phone({ cx, base, w, h, tilt, light, st, children }) {
  const x = cx - w / 2;
  const y = base - h;
  const rx = w * 0.16;
  return (
    <g transform={`rotate(${tilt} ${cx} ${base}) translate(${cx} ${base}) scale(${f1(st.scale * 100) / 100}) translate(${-cx} ${-base})`} opacity={st.op}>
      <ellipse cx={cx} cy={y + h / 2} rx={w * 1.25} ry={h * 0.8} fill="url(#ms-sglow)" opacity={0.5 * st.bright} />
      <rect x={x} y={y} width={w} height={h} rx={rx} fill="#04060a" />
      <rect x={x + 3.5} y={y + 3.5} width={w - 7} height={h - 7} rx={rx * 0.7} fill={`url(#ms-scr-${light ? "l" : "d"})`} opacity={st.bright} />
      <rect x={cx - w * 0.14} y={y + 7} width={w * 0.28} height={3.4} rx={1.7} fill="#04060a" />
      {children && <g opacity={st.bright}>{children}</g>}
      {st.flash > 0.01 && <rect x={x + 3.5} y={y + 3.5} width={w - 7} height={h - 7} rx={rx * 0.7} fill="#f2f9ff" opacity={st.flash} />}
    </g>
  );
}

// One of the swarm: a small phone, kept to a handful of elements.
function Mini({ m, st }) {
  const x = m.cx - m.w / 2;
  const y = m.base - m.h;
  const ink = m.light ? SCR_INK : C.coldHot;
  const bars = R(f1(m.cx - m.w * 0.3), f1(y + m.h * 0.3), f1(m.w * 0.6), f1(m.h * 0.08)) + R(f1(m.cx - m.w * 0.3), f1(y + m.h * 0.62), f1(m.w * 0.6), f1(m.h * 0.12));
  return (
    <g transform={`rotate(${f1(m.tilt)} ${m.cx} ${m.base}) translate(${m.cx} ${m.base}) scale(${f1(st.scale * 100) / 100}) translate(${-m.cx} ${-m.base})`} opacity={st.op}>
      <rect x={x} y={y} width={m.w} height={m.h} rx={f1(m.w * 0.16)} fill="#04060a" />
      <rect
        x={x + 2} y={y + 2} width={m.w - 4} height={m.h - 4} rx={f1(m.w * 0.11)}
        fill={st.flash > 0.3 ? "#f2f9ff" : `url(#ms-scr-${m.light ? "l" : "d"})`} opacity={st.bright}
      />
      {st.more && m.w >= 20 ? (
        <ST x={m.cx} y={f1(y + m.h * 0.56)} s={f1(m.w * 0.24)} c={ink} o={st.bright}>MORE</ST>
      ) : (
        <path d={bars} fill={ink} opacity={0.8 * st.bright} />
      )}
    </g>
  );
}

function B1({ s, local, more }) {
  const { x, y, w, h } = s;
  const cx = x + w / 2;
  const tick = more ? TICKER_MORE : TICKER;
  const tw = tick.length * 11 * 0.72;
  const off = f1((local * 58) % tw);
  return (
    <>
      <defs>
        <clipPath id="ms-tick">
          <rect x={x} y={y + h - 24} width={w} height={24} />
        </clipPath>
      </defs>
      {more ? (
        <ST x={cx} y={y + 104} s={74} c={SCR_INK}>MORE</ST>
      ) : (
        <>
          <circle cx={x + 17} cy={y + 17} r={3.4} fill={SCR_INK} opacity={Math.sin(local * 6) > 0 ? 1 : 0.2} />
          <ST x={x + 26} y={y + 21} s={11} c={SCR_INK} a="start" w={500} sp={0.14}>LIVE</ST>
          <ST x={x + w - 14} y={y + 21} s={11} c={SCR_INK} a="end" w={500} sp={0.14}>ODDS BOOST</ST>
          <rect x={x + 12} y={y + 30} width={w - 24} height={1.2} fill={SCR_INK} opacity={0.35} />
          <ST x={cx} y={y + 88} s={50} c={SCR_INK}>BET NOW</ST>
          <rect x={cx - 52} y={y + 101} width={104} height={20} rx={10} fill={SCR_INK} />
          <ST x={cx} y={y + 115} s={11} c={C.coldHot} w={500}>PARLAY +900</ST>
        </>
      )}
      <rect x={x} y={y + h - 24} width={w} height={24} fill="#0a1724" />
      <g clipPath="url(#ms-tick)">
        <ST x={x - off} y={y + h - 8} s={11} c={C.coldHot} a="start" w={500} sp={0.12}>{tick + tick}</ST>
      </g>
    </>
  );
}

function reelOff(local, i) {
  const T = 2.6;
  const c = Math.floor(local / T);
  const steps = 9 + 3 * i;
  const k = Math.min(1, (local - c * T) / (1.1 + 0.3 * i));
  return ((c * steps + steps * ease.out(k)) % 4) * PITCH;
}

function P1({ s, local, more }) {
  const { cx, base, h } = s;
  const y = base - h;
  if (more) {
    return (
      <>
        <ST x={cx} y={y + h * 0.5} s={20} c={C.coldHot}>MORE</ST>
        <rect x={cx - 25} y={y + h - 38} width={50} height={18} rx={9} fill={C.cold} />
        <ST x={cx} y={y + h - 25} s={9} c={SCR_INK}>MORE</ST>
      </>
    );
  }
  const ry = y + 62;
  const beat = 0.75 + 0.25 * Math.max(0, Math.sin(local * 5));
  return (
    <>
      <defs>
        {[0, 1, 2].map((i) => (
          <clipPath key={i} id={`ms-reel-${i}`}>
            <rect x={cx - 31 + i * 22} y={ry - 15} width={18} height={30} rx={2} />
          </clipPath>
        ))}
      </defs>
      <ST x={cx} y={y + 28} s={8.6} c={C.cold} w={500} sp={0.08}>SPIN TO WIN</ST>
      {[0, 1, 2].map((i) => {
        const rx0 = cx - 31 + i * 22;
        const off = reelOff(local, i);
        let d = "";
        for (let k = -1; k < 7; k++) d += SYM[(((k + i * 3) % 4) + 4) % 4](rx0 + 9, ry + k * PITCH - off);
        return (
          <g key={i}>
            <rect x={rx0} y={ry - 15} width={18} height={30} rx={2} fill="#e3f0fb" />
            <path d={d} fill="#0b1622" fillRule="evenodd" clipPath={`url(#ms-reel-${i})`} />
          </g>
        );
      })}
      <ST x={cx} y={y + 104} s={14} c={C.coldHot}>+900</ST>
      <rect x={cx - 25} y={y + 118} width={50} height={18} rx={9} fill={C.cold} opacity={beat} />
      <ST x={cx} y={y + 131} s={9} c={SCR_INK}>SPIN</ST>
    </>
  );
}

function P2({ s, local, more }) {
  const { cx, base, h } = s;
  const y = base - h;
  if (more) return <ST x={cx} y={y + h * 0.56} s={15} c={SCR_INK}>MORE</ST>;
  const beat = 1 + 0.08 * Math.max(0, Math.sin(local * 5.5));
  const by = y + 92;
  return (
    <>
      <ST x={cx} y={y + 40} s={15} c={SCR_INK}>FREE</ST>
      <ST x={cx} y={y + 58} s={15} c={SCR_INK}>PLAY</ST>
      <rect x={cx - 20} y={y + 67} width={40} height={1.2} fill={SCR_INK} opacity={0.4} />
      <g transform={`translate(${cx} ${by}) scale(${f1(beat * 100) / 100}) translate(${-cx} ${-by})`}>
        <rect x={cx - 19} y={by - 8} width={38} height={16} rx={8} fill={SCR_INK} />
        <ST x={cx} y={by + 3.5} s={8.5} c={C.coldHot} w={500}>TAP</ST>
      </g>
    </>
  );
}

function B2({ s, local, more }) {
  const { x, y, w } = s;
  const cx = x + w / 2;
  if (more) return <ST x={cx} y={y + 57} s={36} c={C.coldHot}>MORE</ST>;
  const i = Math.floor(local / 0.55) % ODDS.length;
  const jump = 1 - clamp((local % 0.55) / 0.18);
  return (
    <>
      <ST x={cx} y={y + 18} s={10} c={C.cold} w={500} sp={0.12}>ODDS BOOST</ST>
      <ST x={cx - 9} y={y + 57} s={30} c={mixColor(C.coldHot, "#ffffff", jump)}>{ODDS[i]}</ST>
      <path d={`M ${x + w - 26} ${y + 50} l 7 -11 l 7 11 Z`} fill={C.cold} />
      <path d={[0, 1, 2].map((k) => R(x + 14 + k * 42, y + 69, 34, 3)).join(" ")} fill={C.cold} opacity={0.45} />
    </>
  );
}

function W1({ s, local, more }) {
  const { x, y, w } = s;
  const cx = x + w / 2;
  if (more) return <ST x={cx} y={y + 44} s={17} c={C.coldHot}>MORE</ST>;
  const ticks = [0, 1, 2].filter((k) => local % 2.4 > 0.5 + k * 0.45);
  return (
    <>
      <ST x={cx} y={y + 16} s={8.5} c={C.cold} w={500} sp={0.1}>PARLAY</ST>
      <ST x={cx} y={y + 36} s={17} c={C.coldHot}>+900</ST>
      <path d={[0, 1, 2].map((k) => R(x + 9, y + 45 + k * 9, 5, 5)).join(" ")} fill="none" stroke={C.cold} strokeWidth={0.8} />
      {ticks.length > 0 && <path d={ticks.map((k) => R(x + 10.5, y + 46.5 + k * 9, 2, 2)).join(" ")} fill={C.coldHot} />}
      <path d={[0, 1, 2].map((k) => R(x + 18, y + 46.5 + k * 9, 38 - k * 8, 2.2)).join(" ")} fill={C.cold} opacity={0.6} />
    </>
  );
}

const TOWER_BULBS = [0, 1].map((g) => {
  let d = "";
  for (let i = 0; i < 15; i++) {
    if (i % 2 !== g) continue;
    d += dot(SCREENS.t1.x + 4, SCREENS.t1.y + 6 + i * 9.8, 1.5) + dot(SCREENS.t1.x + SCREENS.t1.w - 4, SCREENS.t1.y + 6 + i * 9.8, 1.5);
  }
  return d;
});

function T1({ s, local, more }) {
  const { x, y, w, h } = s;
  const cx = x + w / 2;
  const word = more ? "MORE" : "WIN";
  const pitch = h / (word.length + 0.5);
  const chase = Math.floor(local * 6) % 2;
  return (
    <>
      {word.split("").map((ch, i) => (
        <ST key={i} x={cx} y={f1(y + pitch * (i + 0.95))} s={20} c={C.coldHot} sp={0}>{ch}</ST>
      ))}
      {TOWER_BULBS.map((d, g) => <path key={g} d={d} fill={C.coldHot} opacity={g === chase ? 1 : 0.25} />)}
    </>
  );
}

function P3({ s, local, more }) {
  const { cx, base, h } = s;
  const y = base - h;
  if (more) return <ST x={cx} y={y + h * 0.55} s={16} c={C.coldHot}>MORE</ST>;
  const k = (local % 3) / 3;
  const pts = SPARK.map(([u, v]) => [f1(cx - 24 + u * 48), f1(y + 72 - v * 32)]);
  return (
    <>
      <ST x={cx} y={y + 26} s={8.5} c={C.cold} w={500} sp={0.1}>ODDS</ST>
      <Draw d={poly(pts)} p={smooth(clamp(k * 1.4))} stroke={C.coldHot} strokeWidth={1.6} strokeLinejoin="round" />
      <ST x={cx} y={y + 96} s={14} c={C.coldHot}>+250</ST>
      <rect x={cx - 20} y={y + 104} width={40} height={14} rx={7} fill={C.cold} />
      <ST x={cx} y={y + 114} s={8} c={SCR_INK} w={500}>BET</ST>
    </>
  );
}

function Screens({ local, dur, pulse }) {
  const st = Object.fromEntries(Object.entries(SCREENS).map(([id, s]) => [id, popState(local, s.at * dur, s.seed, s.more * dur)]));
  const glow = (o) => o && { ...o, bright: o.bright * pulse };
  const b1 = glow(st.b1);
  const b2 = glow(st.b2);
  const t1 = glow(st.t1);
  const w1 = glow(st.w1);
  return (
    <g>
      {w1 && (
        <g opacity={w1.op}>
          <rect x={SCREENS.w1.x} y={SCREENS.w1.y} width={SCREENS.w1.w} height={SCREENS.w1.h} fill="url(#ms-scr-d)" opacity={w1.bright} />
          <g opacity={w1.bright}><W1 s={SCREENS.w1} local={local} more={w1.more} /></g>
          {w1.flash > 0.01 && <rect x={SCREENS.w1.x} y={SCREENS.w1.y} width={SCREENS.w1.w} height={SCREENS.w1.h} fill="#f2f9ff" opacity={w1.flash} />}
          <rect x={SCREENS.w1.x} y={SCREENS.w1.y} width={SCREENS.w1.w} height={SCREENS.w1.h} fill="none" stroke="#090909" strokeWidth={3} />
        </g>
      )}
      {t1 && (
        <g opacity={t1.op}>
          <ellipse cx={SCREENS.t1.x + 17} cy={SCREENS.t1.y + 74} rx={70} ry={120} fill="url(#ms-sglow)" opacity={0.6 * t1.bright} />
          <rect x={TH_BX - 3} y={SCREENS.t1.y + SCREENS.t1.h} width={6} height={GROUND - SCREENS.t1.y - SCREENS.t1.h} fill="#06080b" />
          <rect x={SCREENS.t1.x - 4} y={SCREENS.t1.y - 4} width={SCREENS.t1.w + 8} height={SCREENS.t1.h + 8} fill="#04060a" />
          <rect x={SCREENS.t1.x} y={SCREENS.t1.y} width={SCREENS.t1.w} height={SCREENS.t1.h} fill="url(#ms-scr-d)" opacity={t1.bright} />
          <g opacity={t1.bright}><T1 s={SCREENS.t1} local={local} more={t1.more} /></g>
          <rect x={SCREENS.t1.x} y={SCREENS.t1.y} width={SCREENS.t1.w} height={SCREENS.t1.h} fill="url(#ms-scan)" />
          {t1.flash > 0.01 && <rect x={SCREENS.t1.x} y={SCREENS.t1.y} width={SCREENS.t1.w} height={SCREENS.t1.h} fill="#f2f9ff" opacity={t1.flash} />}
        </g>
      )}
      {b2 && <Board s={SCREENS.b2} st={b2}><B2 s={SCREENS.b2} local={local} more={b2.more} /></Board>}
      {b1 && <Board s={SCREENS.b1} st={b1}><B1 s={SCREENS.b1} local={local} more={b1.more} /></Board>}
      {SWARM.map((m, i) => {
        const o = glow(popState(local, m.at * dur, m.seed, (0.935 + i * 0.002) * dur));
        return o ? <Mini key={i} m={m} st={o} /> : null;
      })}
      {["p1", "p2", "p3"].map((id) => {
        const o = glow(st[id]);
        if (!o) return null;
        const s = SCREENS[id];
        const Content = { p1: P1, p2: P2, p3: P3 }[id];
        return (
          <Phone key={id} {...s} st={o}>
            <Content s={s} local={local} more={o.more} />
          </Phone>
        );
      })}
    </g>
  );
}

// Cold pools on the rubble and streaks on the wet road under each screen.
const SCREEN_FEET = [
  { id: "b1", x: 748, w: 260, seed: 21 },
  { id: "p1", x: 418, w: 120, seed: 22 },
  { id: "p2", x: 574, w: 90, seed: 23 },
  { id: "b2", x: 243, w: 170, seed: 24 },
  { id: "w1", x: 1078, w: 90, seed: 25 },
  { id: "t1", x: TH_BX, w: 70, seed: 26 },
  { id: "p3", x: 1374, w: 100, seed: 27 },
];

// ---------------------------------------------------------------------------

export default function MainStreet({ t, local, dur, p }) {
  const night = smooth(seg(p, 0.06, 0.25));
  const S = LOTS.map((L) => lotState(L, p, local, dur));
  const ST_ = Object.fromEntries(LOTS.map((L, i) => [L.id, S[i]]));

  // The line: pulses in flight, and how hard the data center runs.
  let glow = 0.32;
  let flash = 0;
  const live = [];
  SOURCES.forEach((src) => {
    const start = src.at * dur + PULSE_LEAD;
    const arrive = start + src.track.len / PULSE_SPEED;
    if (local > start && local < arrive) live.push({ id: src.id, tr: src.track, pos: (local - start) * PULSE_SPEED });
    const d = local - arrive;
    if (d > 0) {
      glow += 0.095 * smooth(clamp(d / 1.2));
      flash += Math.exp(-d * 2.2);
    }
  });
  const hum = 1 + 0.06 * Math.sin(local * 1.7);
  const cold = clamp((glow - 0.32) / 0.7);
  const scr = smooth(seg(p, 0.6, 0.88));
  const moreAll = smooth(seg(p, 0.93, 0.96));
  const pulse = 1 - 0.18 * moreAll * (0.5 + 0.5 * Math.sin(local * 7.5));
  const townLit = S.reduce((a, s) => a + s.lit, 0) / S.length;
  const dcBuild = seg(p, 0.16, 0.3);

  const baseFace = mixColor(INK.facadeDusk, INK.facade, night);
  const face = mixColor(mixColor(baseFace, INK.facadeCold, cold), "#152233", 0.55 * scr);
  const mortar = mixColor(mixColor(INK.mortarDusk, INK.mortar, night), INK.mortarCold, cold);

  // The camera starts close on the masons at the first lot and pulls back as
  // the town goes up around them, then leans in on the screens.
  const pull = ease.inOut(seg(p, 0, 0.27));
  const push = ease.inOut(seg(p, 0.6, 1));
  const z = lerp(1.45, 1, pull) * (1 + 0.08 * push);
  const fx = lerp(lerp(510, 800, pull), 792, push);
  const fy = lerp(lerp(585, 450, pull), 468, push);

  const lamps = LAMPS.map((l) => {
    const on = kindle(local, l.on * dur, l.seed, 0.9);
    const off = failing(local, l.off * dur, l.seed, 1.6);
    const buzz = hash(Math.floor(local * 9) + l.seed) > 0.97 ? 0.45 : 1;
    return on * off * buzz * (1 - 0.3 * cold);
  });

  return (
    <g>
      <defs>
        <linearGradient id="ms-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-700" x2="0" y2="640">
          <stop offset="0" stopColor="#010203" />
          <stop offset="0.6" stopColor={mixColor("#0a0e16", "#05070b", night)} />
          <stop offset="0.86" stopColor={mixColor("#1e1a1d", "#0a0d13", night)} />
          <stop offset="1" stopColor={mixColor("#4d3123", "#111721", night)} />
        </linearGradient>
        <radialGradient id="ms-dusk">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.55" />
          <stop offset="0.45" stopColor={C.warmDeep} stopOpacity="0.25" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-warmhaze">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.5" />
          <stop offset="0.5" stopColor={C.warmDeep} stopOpacity="0.2" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-coldhaze">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.55" />
          <stop offset="0.45" stopColor={C.accent} stopOpacity="0.2" />
          <stop offset="1" stopColor={C.coldDeep} stopOpacity="0" />
        </radialGradient>
        <pattern id="ms-brick" patternUnits="userSpaceOnUse" x="0" y={GROUND} width={BL} height={CH * 2}>
          <path d={`M 0 0.5 H ${BL} M 0 ${CH + 0.5} H ${BL} M 0.5 0 V ${CH} M ${BL / 2 + 0.5} ${CH} V ${CH * 2}`} stroke={mortar} strokeWidth={1} fill="none" />
        </pattern>
        <linearGradient id="ms-lit" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f6c78c" />
          <stop offset="0.55" stopColor="#dc9550" />
          <stop offset="1" stopColor="#9c5726" />
        </linearGradient>
        <linearGradient id="ms-up" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7a4a22" />
          <stop offset="0.3" stopColor="#e4a463" />
          <stop offset="1" stopColor="#b8763b" />
        </linearGradient>
        <linearGradient id="ms-refl" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.5" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ms-reflc" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.55" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ms-band" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1d2a38" stopOpacity="0" />
          <stop offset="1" stopColor="#24364a" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="ms-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.32" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0.02" />
        </linearGradient>
        <radialGradient id="ms-pool">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.42" />
          <stop offset="0.6" stopColor={C.warm} stopOpacity="0.12" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-cpool">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.5" />
          <stop offset="0.6" stopColor={C.accent} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-halo">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.9" />
          <stop offset="0.25" stopColor={C.warm} stopOpacity="0.35" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-sglow">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.45" />
          <stop offset="0.5" stopColor={C.accent} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-pw">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.95" />
          <stop offset="0.3" stopColor={C.warm} stopOpacity="0.35" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-pc">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.95" />
          <stop offset="0.3" stopColor={C.cold} stopOpacity="0.35" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-dust">
          <stop offset="0" stopColor="#8f949a" stopOpacity="0.4" />
          <stop offset="0.6" stopColor="#6d7279" stopOpacity="0.14" />
          <stop offset="1" stopColor="#6d7279" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ms-belfry" cx="0.5" cy="0.4" r="0.7">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.7" />
          <stop offset="1" stopColor={C.coldDeep} stopOpacity="0.9" />
        </radialGradient>
        <linearGradient id="ms-road" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0e0f11" />
          <stop offset="0.25" stopColor="#060708" />
          <stop offset="1" stopColor="#030303" />
        </linearGradient>
        <pattern id="ms-scan" patternUnits="userSpaceOnUse" x="0" y="0" width="6" height="3">
          <rect x="0" y="2" width="6" height="1" fill="#000000" opacity="0.13" />
        </pattern>
        <linearGradient id="ms-scr-l" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eef7ff" />
          <stop offset="1" stopColor="#a6c9e8" />
        </linearGradient>
        <linearGradient id="ms-scr-d" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1b3d5e" />
          <stop offset="1" stopColor="#0a1828" />
        </linearGradient>
      </defs>

      <g transform={`translate(800 450) scale(${z}) translate(${-fx} ${-fy})`}>
        {/* sky */}
        <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#ms-sky)" />
        {night < 1 && <ellipse cx={620} cy={630} rx={1150} ry={300} fill="url(#ms-dusk)" opacity={0.75 * (1 - night)} />}
        <ellipse cx={560} cy={600} rx={980} ry={300} fill="url(#ms-warmhaze)" opacity={0.75 * townLit * night} />
        <ellipse
          cx={1360} cy={430} rx={560 + 260 * cold} ry={330 + 120 * cold}
          fill="url(#ms-coldhaze)" opacity={(0.3 + 0.45 * cold + 0.22 * Math.min(1, flash)) * hum * dcBuild}
        />
        <rect x={BLEED.x} y={250} width={BLEED.w} height={HORIZON - 250} fill="url(#ms-band)" opacity={(0.18 + 0.5 * cold) * night} />
        {STARS.map((g, i) => (
          <path key={i} d={g.d} fill="#dfe7ef" opacity={0.62 * night * (0.6 + 0.4 * Math.sin(local * g.rate + g.ph)) * (1 - 0.45 * cold)} />
        ))}

        {/* far ground and the data center behind the rooftops */}
        <path d={TREELINE} fill={mixColor("#120d0b", "#06070a", night)} />
        <rect x={BLEED.x} y={HORIZON} width={BLEED.w} height={GROUND - HORIZON + 10} fill={mixColor("#0f0b09", "#07080b", night)} />
        {dcBuild > 0 && (
          <g transform={`translate(${DC.x} ${DC.y}) scale(${DC.s})`}>
            <DataCenter id="ms-dc" x={0} y={0} w={DC.w} h={DC.h} build={dcBuild} glow={Math.min(1.5, glow * hum + 0.5 * flash + 0.3 * scr)} t={t} fence={false} />
            {dcBuild < 0.8 && (
              <Draw
                d={`M 0 0 L 0 ${-DC.h} L ${DC.w} ${-DC.h} L ${DC.w} 0`} p={seg(dcBuild, 0, 0.55)}
                stroke={C.cold} strokeWidth={9} strokeLinecap="round" opacity={0.22 * (1 - seg(dcBuild, 0.5, 0.8))}
              />
            )}
            <path d={DC_FENCE} fill="none" stroke="#2a3642" strokeWidth={1.5} opacity={seg(dcBuild, 0.45, 0.8)} />
          </g>
        )}
        {scr > 0 && FAR.some((f) => p > f.at) && (
          <path
            d={FAR.filter((f) => p > f.at).map((f) => R(f1(f.x + f.w / 2 - 0.7), f1(f.y + f.h), 1.4, f1(HORIZON + 2 - f.y - f.h))).join(" ")}
            fill="#0b1119"
          />
        )}
        {scr > 0 && FAR.some((f) => p > f.at) && [0, 1, 2].map((g) => (
          <path
            key={g}
            d={FAR.filter((f) => f.g === g && p > f.at).map((f) => R(f1(f.x), f1(f.y), f1(f.w), f1(f.h))).join(" ")}
            fill={g === 1 ? "#dcecfa" : C.cold}
            opacity={(0.45 + 0.3 * Math.sin(local * (2 + g) + g * 2)) * pulse}
          />
        ))}

        {/* the lots, and what stands on them */}
        {LOTS.map((L, i) => <Footing key={L.id} L={L} S={S[i]} />)}
        {LOTS.map((L, i) => <Lot key={L.id} L={L} S={S[i]} face={face} cold={cold} local={local} />)}
        {LOTS.map((L, i) => (MOUNTS[L.id] || []).map((m, j) => {
          const tf = mountTf(m, L, S[i], local);
          return tf === "" ? <Mount key={`${L.id}${j}`} L={L} m={m} S={S[i]} tf="" local={local} /> : null;
        }))}
        {S.map((s, i) => s.falling && <path key={i} d={s.falling} fill={mixColor("#2e2119", "#1f2832", cold)} />)}
        {SCAFFOLDS.map((sc) => <Scaffold key={sc.lot} L={LOT[sc.lot]} S={ST_[sc.lot]} sc={sc} local={local} />)}

        {/* sidewalk, curb, wet road, and the light on them */}
        <rect x={BLEED.x} y={GROUND} width={BLEED.w} height={CURB - GROUND} fill={mixColor("#141210", "#101113", night)} />
        <path d={`M ${BLEED.x} ${GROUND + 0.5} H ${BLEED.x + BLEED.w}`} stroke="#1c1d20" strokeWidth={1} />
        <rect x={BLEED.x} y={CURB} width={BLEED.w} height={BLEED.y + BLEED.h - CURB} fill="url(#ms-road)" />
        <path d={`M ${BLEED.x} ${CURB} H ${BLEED.x + BLEED.w}`} stroke={mixColor("#2a2522", "#2a3440", cold)} strokeWidth={2} />
        {SHOPS.map((s) => <Spill key={s.id} x={GEO[s.id].win.x} w={GEO[s.id].win.w} k={ST_[s.id].lit} seed={s.seed} />)}
        <Spill x={THEATER.x + 20} w={THEATER.w - 40} k={ST_.theater.lit * 0.9} seed={5} />
        <Spill x={BAR.x + 14} w={68} k={ST_.bar.lit * 0.6} seed={6} />
        <Spill x={CHURCH.x + 40} w={50} k={ST_.church.lit * 0.5} seed={7} />
        <Spill x={HWIN.x} w={HWIN.w} k={ST_.house.lit * 0.8} seed={8} />
        {LAMPS.map((l, i) => <LampLight key={i} x={l.x} k={lamps[i]} />)}
        {scr > 0 && SCREEN_FEET.map((f) => {
          const s = SCREENS[f.id];
          const k = seg(local, s.at * dur, s.at * dur + 0.4) * pulse;
          if (k <= 0) return null;
          return <path key={f.id} d={streak(f.x, f.w * 0.5, f.seed)} fill="url(#ms-reflc)" opacity={0.7 * k} />;
        })}

        {/* the heaps, what fell into them, the dust */}
        {LOTS.map((L, i) => L.mound && <Rubble key={L.id} L={L} F={S[i].fallen} scr={scr} />)}
        {scr > 0 && SCREEN_FEET.map((f) => {
          const s = SCREENS[f.id];
          const k = seg(local, s.at * dur, s.at * dur + 0.4) * pulse;
          if (k <= 0) return null;
          return <ellipse key={f.id} cx={f.x} cy={GROUND + 4} rx={f.w * 0.8} ry={26} fill="url(#ms-cpool)" opacity={0.8 * k} />;
        })}
        {LOTS.map((L, i) => (MOUNTS[L.id] || []).map((m, j) => {
          const tf = mountTf(m, L, S[i], local);
          return tf ? <Mount key={`${L.id}${j}`} L={L} m={m} S={S[i]} tf={tf} local={local} /> : null;
        }))}
        {LOTS.map((L, i) => L.dust && <Dust key={L.id} L={L} S={S[i]} local={local} />)}
        {scr > 0 && <ellipse cx={800} cy={640} rx={900} ry={170} fill="url(#ms-coldhaze)" opacity={0.45 * scr * pulse} />}

        {/* the screens */}
        {p > 0.59 && <Screens local={local} dur={dur} pulse={pulse} />}

        {/* poles, lines, lamps */}
        {SOURCES.map((src) => <DropLine key={src.id} src={src} S={ST_[src.id]} local={local} />)}
        <g fill="none" strokeWidth={1.3}>
          <path d={WIRES[0]} stroke="#0d0e10" />
          <path d={WIRES[1]} stroke="#0d0e10" />
          <path d={WIRES[2]} stroke="#0d0e10" />
        </g>
        {POLES.map((x) => <Pole key={x} x={x} lamp={LAMPS.some((l) => l.x === x)} />)}
        {LAMPS.map((l, i) => (
          <ellipse key={i} cx={l.x + 50} cy={413} rx={7} ry={2} fill={mixColor("#2a1d14", C.warmHot, lamps[i])} />
        ))}
        {live.map((pl) => <Pulse key={pl.id} tr={pl.tr} pos={pl.pos} />)}
        {flash > 0.02 && (
          <circle cx={FEED.a[0]} cy={FEED.a[1]} r={10 + 50 * (1 - Math.min(1, flash))} fill="url(#ms-pc)" opacity={Math.min(1, flash)} />
        )}
      </g>
    </g>
  );
}
